import React, { useCallback, useEffect, useRef, useState } from "react";
import { Block } from "@randajan/jet-react/dom/block";
import { Alert, Box, Typography } from "@mui/material";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";

import { BlimpMuiFormActions } from "../../../../elements/BlimpMuiFormActions/BlimpMuiFormActions";
import { BlimpMuiFormSection } from "../../../../elements/BlimpMuiFormSection/BlimpMuiFormSection";
import { Say } from "../../../../../arc/locale/consts.js";
import { Locale } from "../../../../assets/Locale";
import { useBeforeUnload } from "../../../../assets/hooks/useBeforeUnload.jsx";
import { validateAvatarFileMeta } from "../../../../../arc/forms/avatar.js";
import { AvatarDropZone } from "./AvatarDropZone";
import { AvatarEditor } from "./AvatarEditor";
import { createAvatarFile } from "./avatarCanvas.js";
import { uploadAvatar } from "./avatarUpload.js";

import "./AvatarForm.scss";

const createCrop = _=>({ x:0, y:0 });

const avatarErrorCodes = Object.freeze([
    "avatar_upload_failed",
    "invalid_avatar_dimensions",
    "invalid_avatar_image",
    "invalid_avatar_size",
    "invalid_avatar_type",
    "invalid_avatar_upload",
    "missing_avatar"
]);

const getTransferFile = dataTransfer=>dataTransfer?.files?.[0] || null;
const revokeUrl = url=>{ if (url) { URL.revokeObjectURL(url); } };
const getAvatarErrorId = error=>{
    const code = error?.message || error;
    const errorCode = avatarErrorCodes.includes(code) ? code : "avatar_upload_failed";
    return Say.id("profile", "avatar", "error", errorCode);
};

export const AvatarForm = (props)=>{
    const { onSaved, onCancel } = props;
    const say = Locale.use();
    const dragDepth = useRef(0);
    const [ imageUrl, setImageUrl ] = useState("");
    const [ crop, setCrop ] = useState(createCrop);
    const [ cropPixels, setCropPixels ] = useState(null);
    const [ dragOver, setDragOver ] = useState(false);
    const [ errorId, setErrorId ] = useState("");
    const [ rotation, setRotation ] = useState(0);
    const [ zoom, setZoom ] = useState(1);
    const [ isSubmitting, setIsSubmitting ] = useState(false);

    useBeforeUnload(!!imageUrl || isSubmitting);

    useEffect(()=>()=>revokeUrl(imageUrl), [imageUrl]);

    const resetForm = useCallback(_=>{
        setCrop(createCrop());
        setCropPixels(null);
        setDragOver(false);
        setErrorId("");
        setImageUrl("");
        setRotation(0);
        setZoom(1);
    }, []);

    const setFile = useCallback(file=>{
        const validation = validateAvatarFileMeta(file);
        if (!validation.isValid) {
            setErrorId(getAvatarErrorId(validation.error));
            return;
        }
        setErrorId("");
        setCrop(createCrop());
        setCropPixels(null);
        setRotation(0);
        setZoom(1);
        setImageUrl(URL.createObjectURL(file));
    }, []);

    const dragEnter = event=>{
        event.preventDefault();
        dragDepth.current += 1;
        setDragOver(true);
    };

    const dragLeave = event=>{
        event.preventDefault();
        dragDepth.current = Math.max(0, dragDepth.current - 1);
        setDragOver(!!dragDepth.current);
    };

    const dragOverForm = event=>{
        event.preventDefault();
        event.dataTransfer.dropEffect = "copy";
    };

    const drop = event=>{
        event.preventDefault();
        dragDepth.current = 0;
        setDragOver(false);
        setFile(getTransferFile(event.dataTransfer));
    };

    const completeCrop = useCallback((_area, areaPixels)=>{
        setCropPixels(areaPixels);
    }, []);

    const submit = async event=>{
        event.preventDefault();
        if (!imageUrl || !cropPixels) {
            setErrorId(getAvatarErrorId("missing_avatar"));
            return;
        }
        setIsSubmitting(true);
        try {
            const file = await createAvatarFile(imageUrl, cropPixels, rotation);
            await uploadAvatar(file);
            setErrorId("");
            setIsSubmitting(false);
            onSaved();
        } catch(error) {
            setErrorId(getAvatarErrorId(error));
            setIsSubmitting(false);
        }
    };

    const isEmpty = !imageUrl;
    const title = say(imageUrl ? "profileAvatarSectionEdit" : "profileAvatarActionUpload");
    const submitLabel = isSubmitting ? say("profileAvatarActionSending") : say("profileAvatarActionSave");
    const dragHandlers = isEmpty ? {
        onDragEnter:dragEnter,
        onDragLeave:dragLeave,
        onDragOver:dragOverForm,
        onDrop:drop
    } : {};

    return (
        <Block level={0} className="AvatarForm popScreen" caption={say("profileAvatarCaption")}>
            <Box className="BlimpMuiForm AvatarForm-form" component="form" onSubmit={submit} noValidate {...dragHandlers}>
                <Typography className="BlimpMuiForm-intro">
                    {say("profileAvatarIntro")}
                </Typography>
                {errorId && <Alert severity="error">{say(errorId)}</Alert>}
                <Box className="BlimpMuiForm-sections">
                    <BlimpMuiFormSection bodyClassName="AvatarForm-body" icon={ImageOutlinedIcon} title={title}>
                        {isEmpty && <AvatarDropZone dragOver={dragOver} onBrowse={setFile} />}
                        {imageUrl && (
                            <AvatarEditor
                                crop={crop}
                                imageUrl={imageUrl}
                                onCropChange={setCrop}
                                onCropComplete={completeCrop}
                                onRotationChange={setRotation}
                                onZoomChange={setZoom}
                                rotation={rotation}
                                zoom={zoom}
                            />
                        )}
                    </BlimpMuiFormSection>
                </Box>
                {!isEmpty && (
                    <BlimpMuiFormActions
                        onCancel={resetForm}
                        submitDisabled={isSubmitting}
                        submitLabel={submitLabel}
                    />
                )}
            </Box>
        </Block>
    );
};
